import { isEmpty, mapValues } from 'lodash'
import { getApiUrlForEnv } from '../utils/ApiUrl'

const requestErrors = {}
function getRequestKey({
  url = '',
  method = '',
  responseType = '',
  body = '',
} = {}) {
  return [url, method.toUpperCase(), responseType, body].join('||')
}

function IsJsonString(str) {
  try {
    JSON.parse(str)
  } catch (e) {
    return false
  }
  return true
}

class Fetch {
  private readonly accessToken: string
  public readonly abortController: AbortController

  public constructor(accessToken: string) {
    this.accessToken = accessToken
    this.abortController = new AbortController()
  }

  public fetch = async (
    input: RequestInfo,
    init?: RequestInit,
  ): Promise<any> => {
    const req = typeof input === 'string' ? this.buildUrl(input) : input

    return await fetch(req, init)
  }

  public authorizedFetch = async (
    input: RequestInfo,
    { body, ...init }: RequestInit = {},
  ): Promise<Response> => {
    const requestKeyToUse = getRequestKey({
      body: (body as any) || '',
      method: init.method || '',
      url: typeof input === 'string' ? input : input.url,
    })

    const obj =
      body && IsJsonString(body as string) ? JSON.parse(body as string) : {}
    const response = await this.fetch(input, {
      body: IsJsonString(body as string) ? JSON.stringify(deepTrim(obj)) : body,
      ...init,
      headers: {
        ...((init && init.headers) || {}),
        Authorization: `Bearer ${this.accessToken}`,
        'X-Content-Type-Options': 'nosniff',
        // 'Content-Type': 'application/json',
        'access-control-expose-headers':
          'date, Content-Type, X-Content-Type-Options',
      },
      signal: this.abortController.signal,
    })

    if (response.status === 401) {
      const json = await response.json()
      throw new UnauthorizedError(
        'Unauthorized',
        json.messages &&
        json.messages.length &&
        json.messages[0] !== 'Unauthorized'
          ? 'Free Account'
          : 'Deleted Account Or Data Changed',
      )
    }

    if (response.status >= 500) {
      if (!requestErrors[requestKeyToUse]) {
        requestErrors[requestKeyToUse] = 1
      }
      requestErrors[requestKeyToUse]++

      if (
        !requestKeyToUse.includes('airbnb') &&
        requestErrors[requestKeyToUse] >= 3
      ) {
        throw new Error('Highly unavailable')
      }
    } else {
      delete requestErrors[requestKeyToUse]
    }

    return response
  }

  private buildUrl = (url: string): string => {
    const EnvUrl = getApiUrlForEnv()
    return process.env.NODE_ENV === 'production'
      ? url.startsWith('/')
        ? `${EnvUrl}${url}`
        : `${EnvUrl}${'/'}${url}`
      : url
  }
}

export class UnauthorizedError extends Error {
  constructor(message, public reason) {
    super(message)
  }
}

function deepTrim(obj) {
  return mapValues(obj, val =>
    typeof val === 'string'
      ? val.trim()
      : typeof val === 'object' && !isEmpty(val)
      ? deepTrim(val)
      : val,
  )
}

export default function(accessToken: string) {
  return new Fetch(accessToken)
}
